/**
 * Lazy Loading Components and Utilities
 * Provides intelligent code splitting and lazy loading for React components
 */

import React, { Suspense, ComponentType, LazyExoticComponent } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

// Loading components for different contexts
export const ComponentLoadingSpinner: React.FC<{ message?: string }> = ({
  message = 'Loading...'
}) => (
  <div className="flex items-center justify-center h-64">
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">{message}</p>
    </div>
  </div>
);

export const PageLoadingSpinner: React.FC<{ message?: string }> = ({
  message = 'Loading page...'
}) => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-6"></div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">{message}</h2>
      <p className="text-gray-600">Please wait while we load the content</p>
    </div>
  </div>
);

export const ModalLoadingSpinner: React.FC<{ message?: string }> = ({
  message = 'Loading...'
}) => (
  <div className="flex items-center justify-center p-8">
    <div className="text-center">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-3"></div>
      <p className="text-sm text-gray-600">{message}</p>
    </div>
  </div>
);

// Error fallback components
export const ComponentErrorFallback: React.FC<{
  error: Error;
  resetErrorBoundary: () => void;
  componentName?: string;
}> = ({ error, resetErrorBoundary, componentName = 'component' }) => (
  <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
    <h3 className="text-lg font-semibold text-red-800 mb-2">
      Failed to load {componentName}
    </h3>
    <p className="text-sm text-red-600 mb-4">{error.message}</p>
    <button
      onClick={resetErrorBoundary}
      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
    >
      Try again
    </button>
  </div>
);

export const PageErrorFallback: React.FC<{
  error: Error;
  resetErrorBoundary: () => void;
  pageName?: string;
}> = ({ error, resetErrorBoundary, pageName = 'page' }) => (
  <div className="min-h-screen flex items-center justify-center p-6">
    <div className="max-w-md w-full bg-red-50 border border-red-200 rounded-lg p-6 text-center">
      <div className="text-red-600 mb-4">
        <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-red-800 mb-2">
        Failed to load {pageName}
      </h2>
      <p className="text-sm text-red-600 mb-4">{error.message}</p>
      <button
        onClick={resetErrorBoundary}
        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
      >
        Reload page
      </button>
    </div>
  </div>
);

// Higher-order component for lazy loading with error boundaries
export function withLazyLoading<P extends object>(
  Component: LazyExoticComponent<ComponentType<P>>,
  options: {
    fallback?: React.ComponentType;
    errorFallback?: React.ComponentType<any>;
    componentName?: string;
    preload?: boolean;
  } = {}
): React.ComponentType<P> {
  const {
    fallback: Fallback = ComponentLoadingSpinner,
    errorFallback: ErrorFallback = ComponentErrorFallback,
    componentName = 'component',
    preload = false
  } = options;

  // Preload the component if requested
  if (preload && typeof window !== 'undefined') {
    // Trigger the lazy load immediately but don't block rendering
    Component.preload?.();
  }

  return function LazyWrapper(props: P) {
    return (
      <ErrorBoundary
        FallbackComponent={(errorProps) => (
          <ErrorFallback {...errorProps} componentName={componentName} />
        )}
        onError={(error, errorInfo) => {
          console.error(`Error loading ${componentName}:`, error, errorInfo);
        }}
      >
        <Suspense fallback={<Fallback />}>
          <Component {...props} />
        </Suspense>
      </ErrorBoundary>
    );
  };
}

// Higher-order component for page-level lazy loading
export function withPageLazyLoading<P extends object>(
  Component: LazyExoticComponent<ComponentType<P>>,
  options: {
    pageName?: string;
    preload?: boolean;
  } = {}
): React.ComponentType<P> {
  const { pageName = 'page', preload = false } = options;

  // Preload the component if requested
  if (preload && typeof window !== 'undefined') {
    Component.preload?.();
  }

  return function PageLazyWrapper(props: P) {
    return (
      <ErrorBoundary
        FallbackComponent={(errorProps) => (
          <PageErrorFallback {...errorProps} pageName={pageName} />
        )}
        onError={(error, errorInfo) => {
          console.error(`Error loading ${pageName}:`, error, errorInfo);
        }}
      >
        <Suspense fallback={<PageLoadingSpinner message={`Loading ${pageName}...`} />}>
          <Component {...props} />
        </Suspense>
      </ErrorBoundary>
    );
  };
}

// Utility for creating lazy-loaded components with built-in error handling
export function createLazyComponent<P extends object>(
  importFunction: () => Promise<{ default: ComponentType<P> }>,
  options: {
    componentName?: string;
    retryCount?: number;
    retryDelay?: number;
  } = {}
): LazyExoticComponent<ComponentType<P>> {
  const { componentName = 'component', retryCount = 3, retryDelay = 1000 } = options;

  // Enhanced import function with retry logic
  const retryableImport = async (attempt = 1): Promise<{ default: ComponentType<P> }> => {
    try {
      return await importFunction();
    } catch (error) {
      if (attempt < retryCount) {
        console.warn(`Failed to load ${componentName}, retrying... (${attempt}/${retryCount})`);
        await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
        return retryableImport(attempt + 1);
      }
      console.error(`Failed to load ${componentName} after ${retryCount} attempts:`, error);
      throw error;
    }
  };

  return React.lazy(retryableImport);
}

// Preloading utilities
export class ComponentPreloader {
  private static loadedComponents = new Set<string>();
  private static preloadPromises = new Map<string, Promise<any>>();

  /**
   * Preload a lazy component
   */
  static preload<T>(
    component: LazyExoticComponent<T>,
    componentId: string
  ): Promise<void> {
    if (this.loadedComponents.has(componentId)) {
      return Promise.resolve();
    }

    if (this.preloadPromises.has(componentId)) {
      return this.preloadPromises.get(componentId)!;
    }

    const promise = component.preload?.().then(() => {
      this.loadedComponents.add(componentId);
      this.preloadPromises.delete(componentId);
    }).catch(error => {
      console.error(`Failed to preload component ${componentId}:`, error);
      this.preloadPromises.delete(componentId);
      throw error;
    });

    if (promise) {
      this.preloadPromises.set(componentId, promise);
      return promise;
    }

    return Promise.resolve();
  }

  /**
   * Preload multiple components
   */
  static preloadMultiple(
    components: Array<{
      component: LazyExoticComponent<any>;
      id: string;
    }>
  ): Promise<void[]> {
    return Promise.all(
      components.map(({ component, id }) => this.preload(component, id))
    );
  }

  /**
   * Preload components on user interaction (hover, focus)
   */
  static setupInteractivePreloading(
    element: HTMLElement,
    component: LazyExoticComponent<any>,
    componentId: string
  ): () => void {
    let timeoutId: number;

    const handleMouseEnter = () => {
      timeoutId = window.setTimeout(() => {
        this.preload(component, componentId);
      }, 100); // Small delay to avoid unnecessary preloads
    };

    const handleMouseLeave = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };

    const handleFocus = () => {
      this.preload(component, componentId);
    };

    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('mouseleave', handleMouseLeave);
    element.addEventListener('focus', handleFocus);

    // Return cleanup function
    return () => {
      element.removeEventListener('mouseenter', handleMouseEnter);
      element.removeEventListener('mouseleave', handleMouseLeave);
      element.removeEventListener('focus', handleFocus);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }

  /**
   * Check if component is already loaded
   */
  static isLoaded(componentId: string): boolean {
    return this.loadedComponents.has(componentId);
  }

  /**
   * Clear preload cache
   */
  static clearCache(): void {
    this.loadedComponents.clear();
    this.preloadPromises.clear();
  }
}

// Hook for intersection-based preloading
export function useIntersectionPreload(
  component: LazyExoticComponent<any>,
  componentId: string,
  options: IntersectionObserverInit = {}
) {
  const [ref, setRef] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    if (!ref) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            ComponentPreloader.preload(component, componentId);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '100px', // Start loading 100px before element comes into view
        ...options
      }
    );

    observer.observe(ref);

    return () => {
      observer.disconnect();
    };
  }, [ref, component, componentId, options]);

  return setRef;
}

// Performance monitoring for lazy loading
export class LazyLoadingMetrics {
  private static metrics = new Map<string, {
    loadTime: number;
    loadAttempts: number;
    successfulLoads: number;
    failedLoads: number;
    lastLoadTime: number;
  }>();

  static recordLoadStart(componentId: string): void {
    const existing = this.metrics.get(componentId) || {
      loadTime: 0,
      loadAttempts: 0,
      successfulLoads: 0,
      failedLoads: 0,
      lastLoadTime: 0
    };

    existing.loadAttempts++;
    existing.lastLoadTime = Date.now();
    this.metrics.set(componentId, existing);
  }

  static recordLoadSuccess(componentId: string): void {
    const metric = this.metrics.get(componentId);
    if (metric) {
      const loadTime = Date.now() - metric.lastLoadTime;
      metric.loadTime = (metric.loadTime + loadTime) / 2; // Moving average
      metric.successfulLoads++;
      this.metrics.set(componentId, metric);
    }
  }

  static recordLoadFailure(componentId: string): void {
    const metric = this.metrics.get(componentId);
    if (metric) {
      metric.failedLoads++;
      this.metrics.set(componentId, metric);
    }
  }

  static getMetrics(): Map<string, any> {
    return new Map(this.metrics);
  }

  static getComponentMetric(componentId: string) {
    return this.metrics.get(componentId);
  }

  static clearMetrics(): void {
    this.metrics.clear();
  }
}